import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { Search, Star, Clock, PlusCircle, ScanLine, Sparkles, Loader2, Trash2, ChevronRight, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const SourceBadge = ({ source }) => {
  const map = {
    custom: { label: "Custom", cls: "bg-purple-100 text-purple-700" },
    basic: { label: "Basic", cls: "bg-emerald-100 text-emerald-700" },
    off: { label: "OFF", cls: "bg-blue-100 text-blue-700" },
    ai: { label: "AI", cls: "bg-amber-100 text-amber-700" },
  };
  const s = map[source] || { label: source || "?", cls: "bg-gray-100 text-gray-700" };
  return <Badge variant="secondary" className={`${s.cls} text-[10px] px-1.5 py-0`}>{s.label}</Badge>;
};

const FoodRow = ({ food, onPick, onToggleFav, isFav, onRemoveFav, onRemoveCustom }) => (
  <button
    type="button"
    onClick={() => onPick(food)}
    className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-800 hover:border-emerald-400 hover:bg-emerald-50/40 dark:hover:bg-emerald-900/10 transition-all group"
    data-testid="food-search-row"
  >
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-gray-900 dark:text-white truncate">{food.name}</span>
          <SourceBadge source={food.source} />
        </div>
        {food.brand && <div className="text-[11px] text-gray-500 truncate">{food.brand}</div>}
        <div className="text-[11px] text-gray-500 mt-0.5">
          {food.serving_description || `Per ${food.serving_size}${food.serving_unit || "g"}`}
        </div>
        <div className="text-xs text-gray-700 dark:text-gray-300 mt-1 tabular-nums">
          <span className="font-bold text-orange-600">{Math.round(food.calories)}</span>
          <span className="text-gray-500"> kcal </span>
          <span className="mx-1 text-gray-300">·</span>
          P <span className="font-semibold">{Math.round(food.protein_g)}g</span>
          <span className="mx-1 text-gray-300">·</span>
          C <span className="font-semibold">{Math.round(food.carbs_g)}g</span>
          <span className="mx-1 text-gray-300">·</span>
          F <span className="font-semibold">{Math.round(food.fats_g)}g</span>
        </div>
      </div>
      <div className="flex flex-col items-center gap-1 shrink-0">
        {onToggleFav && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onToggleFav(food); }}
            className={`p-1 rounded ${isFav ? "text-amber-500" : "text-gray-300 hover:text-amber-400"}`}
            title={isFav ? "Remove favorite" : "Save favorite"}
            data-testid="food-fav-toggle"
          >
            <Star className={`w-4 h-4 ${isFav ? "fill-current" : ""}`} />
          </span>
        )}
        {onRemoveFav && (
          <span role="button" onClick={(e) => { e.stopPropagation(); onRemoveFav(); }} className="p-1 rounded text-red-400 hover:text-red-600">
            <Trash2 className="w-4 h-4" />
          </span>
        )}
        {onRemoveCustom && (
          <span role="button" onClick={(e) => { e.stopPropagation(); onRemoveCustom(); }} className="p-1 rounded text-red-400 hover:text-red-600">
            <Trash2 className="w-4 h-4" />
          </span>
        )}
        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-emerald-500" />
      </div>
    </div>
  </button>
);

// ---------- Barcode Scanner Tab ----------
function BarcodeScanner({ onFound }) {
  const scannerId = "food-barcode-reader";
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const [error, setError] = useState("");
  const scannerRef = useRef(null);

  const stop = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.clear(); } catch { /* noop */ }
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  const lookup = async (code) => {
    try {
      const res = await axios.get(`${API}/nutrition/foods/barcode/${encodeURIComponent(code)}`, { headers: authHeaders() });
      onFound(res.data);
    } catch {
      toast.error(`Barcode ${code} not found`);
    }
  };

  const start = async () => {
    setError("");
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      await stop();
      const scanner = new Html5Qrcode(scannerId);
      scannerRef.current = scanner;
      setScanning(true);
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 140 } },
        async (decodedText) => {
          await stop();
          await lookup(decodedText.trim());
        },
        () => { /* per-frame errors ignored */ }
      );
    } catch (e) {
      setScanning(false);
      setError(e?.message || "Camera unavailable");
    }
  };

  useEffect(() => () => { stop(); }, [stop]);

  return (
    <div className="space-y-3">
      <div id={scannerId} className="w-full min-h-[220px] rounded-lg bg-gray-900/5 dark:bg-gray-900/60 flex items-center justify-center overflow-hidden">
        {!scanning && <ScanLine className="w-12 h-12 text-gray-400" />}
      </div>

      {!scanning ? (
        <Button onClick={start} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="barcode-start-btn">
          <ScanLine className="w-4 h-4 mr-2" /> Start camera
        </Button>
      ) : (
        <Button onClick={stop} variant="outline" className="w-full">
          <X className="w-4 h-4 mr-2" /> Stop
        </Button>
      )}

      {error && <div className="text-xs text-red-500">{error}</div>}

      <div className="pt-2 border-t border-gray-200 dark:border-gray-800">
        <Label htmlFor="manual-barcode" className="text-xs font-medium">Or enter barcode manually</Label>
        <div className="flex gap-2 mt-1">
          <Input id="manual-barcode" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="e.g. 3017620422003" data-testid="manual-barcode-input" />
          <Button onClick={() => manual && lookup(manual.trim())} data-testid="manual-barcode-lookup">Look up</Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Custom food form ----------
function CustomFoodForm({ onSaved }) {
  const [form, setForm] = useState({ name: "", brand: "", serving_size: 100, serving_unit: "g", serving_description: "", calories: "", protein_g: "", carbs_g: "", fats_g: "" });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || form.calories === "") { toast.error("Name and calories are required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        brand: form.brand.trim() || null,
        serving_size: Number(form.serving_size) || 100,
        serving_unit: form.serving_unit || "g",
        serving_description: form.serving_description || null,
        calories: Number(form.calories) || 0,
        protein_g: Number(form.protein_g) || 0,
        carbs_g: Number(form.carbs_g) || 0,
        fats_g: Number(form.fats_g) || 0,
      };
      const res = await axios.post(`${API}/nutrition/foods/custom`, payload, { headers: authHeaders() });
      toast.success("Custom food saved");
      setForm({ name: "", brand: "", serving_size: 100, serving_unit: "g", serving_description: "", calories: "", protein_g: "", carbs_g: "", fats_g: "" });
      onSaved(res.data);
    } catch { toast.error("Failed to save custom food"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-2 border border-dashed border-emerald-300 rounded-lg p-3 bg-emerald-50/40">
      <h4 className="text-sm font-semibold flex items-center gap-1"><PlusCircle className="w-4 h-4 text-emerald-600" /> New custom food</h4>
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Name*" value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="custom-food-name" />
        <Input placeholder="Brand (optional)" value={form.brand} onChange={(e) => upd("brand", e.target.value)} />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Input type="number" placeholder="Serving size" value={form.serving_size} onChange={(e) => upd("serving_size", e.target.value)} />
        <Input placeholder="Unit (g, ml, cup)" value={form.serving_unit} onChange={(e) => upd("serving_unit", e.target.value)} />
        <Input placeholder="e.g. 1 cup" value={form.serving_description} onChange={(e) => upd("serving_description", e.target.value)} />
      </div>
      <div className="grid grid-cols-4 gap-2">
        <Input type="number" placeholder="kcal*" value={form.calories} onChange={(e) => upd("calories", e.target.value)} data-testid="custom-food-calories" />
        <Input type="number" placeholder="Protein g" value={form.protein_g} onChange={(e) => upd("protein_g", e.target.value)} />
        <Input type="number" placeholder="Carbs g" value={form.carbs_g} onChange={(e) => upd("carbs_g", e.target.value)} />
        <Input type="number" placeholder="Fats g" value={form.fats_g} onChange={(e) => upd("fats_g", e.target.value)} />
      </div>
      <Button size="sm" onClick={save} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="custom-food-save">
        {saving ? "Saving…" : "Save custom food"}
      </Button>
    </div>
  );
}

// ---------- Servings picker (once a food is chosen) ----------
function ServingsPicker({ food, meal, onAdd, onBack }) {
  const [servings, setServings] = useState(1);
  const s = Math.max(0, Number(servings) || 0);
  return (
    <div className="space-y-4">
      <button className="text-xs text-emerald-600 hover:underline" onClick={onBack} data-testid="servings-back-btn">← Back to search</button>
      <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100">
        <div className="font-semibold">{food.name}</div>
        {food.brand && <div className="text-xs text-gray-600">{food.brand}</div>}
        <div className="text-xs text-gray-500">{food.serving_description || `Per ${food.serving_size}${food.serving_unit || "g"}`}</div>
      </div>
      <div>
        <Label className="text-xs font-medium">Servings</Label>
        <Input type="number" min="0" step="0.25" value={servings} onChange={(e) => setServings(e.target.value)} data-testid="servings-input" />
      </div>
      <div className="grid grid-cols-4 gap-2 text-xs">
        <div className="bg-orange-50 rounded p-2 text-center">
          <div className="text-[10px] text-orange-600 font-semibold uppercase">Cal</div>
          <div className="font-bold tabular-nums">{Math.round(food.calories * s)}</div>
        </div>
        <div className="bg-emerald-50 rounded p-2 text-center">
          <div className="text-[10px] text-emerald-600 font-semibold uppercase">Prot</div>
          <div className="font-bold tabular-nums">{Math.round(food.protein_g * s)}g</div>
        </div>
        <div className="bg-blue-50 rounded p-2 text-center">
          <div className="text-[10px] text-blue-600 font-semibold uppercase">Carb</div>
          <div className="font-bold tabular-nums">{Math.round(food.carbs_g * s)}g</div>
        </div>
        <div className="bg-purple-50 rounded p-2 text-center">
          <div className="text-[10px] text-purple-600 font-semibold uppercase">Fat</div>
          <div className="font-bold tabular-nums">{Math.round(food.fats_g * s)}g</div>
        </div>
      </div>
      <Button onClick={() => onAdd(food, s)} disabled={s <= 0} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="add-food-confirm">
        Add to {meal}
      </Button>
    </div>
  );
}

// ---------- Main Dialog ----------
export default function FoodSearchDialog({ open, onOpenChange, meal, date, onEntryAdded }) {
  const [tab, setTab] = useState("search");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [aiHint, setAiHint] = useState(false);
  const [recent, setRecent] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [customFoods, setCustomFoods] = useState([]);
  const [aiQuery, setAiQuery] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [picked, setPicked] = useState(null);

  const loadLists = useCallback(async () => {
    try {
      const [recRes, favRes, cfRes] = await Promise.all([
        axios.get(`${API}/nutrition/recent`, { headers: authHeaders() }),
        axios.get(`${API}/nutrition/favorites`, { headers: authHeaders() }),
        axios.get(`${API}/nutrition/foods/custom`, { headers: authHeaders() }),
      ]);
      setRecent(recRes.data || []);
      setFavorites(favRes.data || []);
      setCustomFoods(cfRes.data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (open) {
      setTab("search"); setQ(""); setResults([]); setPicked(null); setAiQuery(""); setAiResult(null);
      loadLists();
    }
  }, [open, loadLists]);

  const runSearch = useCallback(async (query, withAI = false) => {
    if (!query || query.trim().length < 2) { setResults([]); return; }
    setSearching(true); setAiHint(false);
    try {
      const res = await axios.get(`${API}/nutrition/foods/search`, {
        params: { q: query, include_ai: withAI ? 1 : 0 },
        headers: authHeaders(),
      });
      const r = res.data.results || [];
      setResults(r);
      if (r.length === 0 && !withAI) setAiHint(true);
    } catch { toast.error("Search failed"); }
    finally { setSearching(false); }
  }, []);

  // Debounced search
  useEffect(() => {
    if (tab !== "search") return;
    const t = setTimeout(() => runSearch(q, false), 350);
    return () => clearTimeout(t);
  }, [q, tab, runSearch]);

  const isFav = (food) => favorites.some((f) => (f.food?.name === food.name) && (f.food?.external_id || null) === (food.external_id || null));

  const toggleFav = async (food) => {
    const existing = favorites.find((f) => f.food?.name === food.name && (f.food?.external_id || null) === (food.external_id || null));
    if (existing) {
      await axios.delete(`${API}/nutrition/favorites/${existing.id}`, { headers: authHeaders() });
      toast.success("Removed from favorites");
    } else {
      await axios.post(`${API}/nutrition/favorites`, { food }, { headers: authHeaders() });
      toast.success("Saved to favorites");
    }
    loadLists();
  };

  const askAI = async () => {
    if (!aiQuery.trim()) return;
    setAiLoading(true); setAiResult(null);
    try {
      const res = await axios.post(`${API}/nutrition/foods/ai-lookup`, { query: aiQuery.trim() }, { headers: authHeaders() });
      setAiResult(res.data);
    } catch { toast.error("AI estimate unavailable"); }
    finally { setAiLoading(false); }
  };

  const removeCustom = async (id) => {
    await axios.delete(`${API}/nutrition/foods/custom/${id}`, { headers: authHeaders() });
    toast.success("Custom food removed");
    loadLists();
  };

  const addEntry = async (food, servings) => {
    try {
      const res = await axios.post(`${API}/nutrition/entries`, {
        date, meal, servings, food,
      }, { headers: authHeaders() });
      toast.success(`Added to ${meal}`);
      onEntryAdded(res.data);
      onOpenChange(false);
    } catch { toast.error("Failed to add entry"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="food-search-dialog">
        <DialogHeader>
          <DialogTitle className="capitalize">Add food · {meal}</DialogTitle>
        </DialogHeader>

        {picked ? (
          <ServingsPicker food={picked} meal={meal} onAdd={addEntry} onBack={() => setPicked(null)} />
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid grid-cols-5 shrink-0">
              <TabsTrigger value="search" data-testid="tab-search"><Search className="w-3.5 h-3.5 mr-1" /><span className="hidden sm:inline">Search</span></TabsTrigger>
              <TabsTrigger value="recent" data-testid="tab-recent"><Clock className="w-3.5 h-3.5 mr-1" /><span className="hidden sm:inline">Recent</span></TabsTrigger>
              <TabsTrigger value="favorites" data-testid="tab-favorites"><Star className="w-3.5 h-3.5 mr-1" /><span className="hidden sm:inline">Favs</span></TabsTrigger>
              <TabsTrigger value="mine" data-testid="tab-mine"><PlusCircle className="w-3.5 h-3.5 mr-1" /><span className="hidden sm:inline">Mine</span></TabsTrigger>
              <TabsTrigger value="scan" data-testid="tab-scan"><ScanLine className="w-3.5 h-3.5 mr-1" /><span className="hidden sm:inline">Scan</span></TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto pt-3 pr-1">
              <TabsContent value="search" className="mt-0 space-y-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    autoFocus
                    placeholder="Search foods (e.g. banana, oatmeal)"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="pl-9"
                    data-testid="food-search-input"
                  />
                </div>
                {searching && <div className="flex items-center gap-2 text-xs text-gray-500"><Loader2 className="w-3 h-3 animate-spin" /> Searching…</div>}
                {!searching && results.length === 0 && q.length >= 2 && (
                  <div className="text-center py-6 space-y-3">
                    <p className="text-sm text-gray-500">No matches.</p>
                    <Button size="sm" onClick={() => runSearch(q, true)} className="bg-amber-500 hover:bg-amber-600" data-testid="food-ai-fallback-btn">
                      <Sparkles className="w-4 h-4 mr-1" /> Ask AI to estimate "{q.slice(0, 30)}"
                    </Button>
                  </div>
                )}
                <div className="space-y-2">
                  {results.map((f, i) => (
                    <FoodRow
                      key={`${f.source}-${f.external_id || f.name}-${i}`}
                      food={f}
                      onPick={setPicked}
                      onToggleFav={toggleFav}
                      isFav={isFav(f)}
                    />
                  ))}
                </div>
                {aiHint && <div className="text-[11px] text-gray-500 text-center">Tip: use the AI button above for anything not in the database.</div>}
              </TabsContent>

              <TabsContent value="recent" className="mt-0 space-y-2">
                {recent.length === 0 && <p className="text-sm text-gray-500 py-6 text-center">No recent foods yet.</p>}
                {recent.map((f, i) => (
                  <FoodRow key={i} food={f} onPick={setPicked} onToggleFav={toggleFav} isFav={isFav(f)} />
                ))}
              </TabsContent>

              <TabsContent value="favorites" className="mt-0 space-y-2">
                {favorites.length === 0 && <p className="text-sm text-gray-500 py-6 text-center">Star foods to save them here.</p>}
                {favorites.map((f) => (
                  <FoodRow
                    key={f.id}
                    food={f.food}
                    onPick={setPicked}
                    onRemoveFav={async () => { await axios.delete(`${API}/nutrition/favorites/${f.id}`, { headers: authHeaders() }); loadLists(); }}
                  />
                ))}
              </TabsContent>

              <TabsContent value="mine" className="mt-0 space-y-3">
                <CustomFoodForm onSaved={() => loadLists()} />
                <div className="space-y-2">
                  {customFoods.map((f) => (
                    <FoodRow
                      key={f.id}
                      food={{ ...f, source: "custom", external_id: f.id }}
                      onPick={setPicked}
                      onRemoveCustom={() => removeCustom(f.id)}
                    />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="scan" className="mt-0">
                <BarcodeScanner onFound={setPicked} />
              </TabsContent>
            </div>
          </Tabs>
        )}

        {/* Floating "Ask AI" panel when on Search tab and user wants direct AI */}
        {!picked && tab === "search" && (
          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-800 space-y-2">
            <div className="text-xs text-gray-500 font-medium flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-amber-500" /> Or describe a meal for AI to estimate</div>
            <div className="flex gap-2">
              <Input placeholder='e.g. "chicken tikka masala with rice"' value={aiQuery} onChange={(e) => setAiQuery(e.target.value)} data-testid="ai-query-input" />
              <Button onClick={askAI} disabled={aiLoading} className="bg-amber-500 hover:bg-amber-600 shrink-0" data-testid="ai-query-btn">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Estimate"}
              </Button>
            </div>
            {aiResult && (
              <FoodRow food={aiResult} onPick={setPicked} onToggleFav={toggleFav} isFav={isFav(aiResult)} />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
